import { requestJson } from "@/lib/api";
import type { CatalogCategory, CatalogCutType, CatalogUnit, Product, Category } from "@/lib/types";

export interface CatalogSnapshot {
  categories: CatalogCategory[];
  cutTypes: CatalogCutType[];
  units: CatalogUnit[];
  products: Product[];
}

export interface CatalogMetadata {
  categories: CatalogCategory[];
  cutTypes: CatalogCutType[];
  units: CatalogUnit[];
}

interface CatalogCategoryDto {
  id: string;
  code: string;
  name: string;
  slug: string;
  icon: string;
  color: string;
  sortOrder: number;
  operatorOnly: boolean;
  active: boolean;
}

interface CatalogCutTypeDto {
  id: string;
  name: string;
  active: boolean;
}

interface CatalogUnitDto {
  id: string;
  name: string;
  conversionToKg: number;
  active: boolean;
  group: string | null;
}

interface CatalogProductDto {
  id: string;
  name: string;
  category: CatalogCategoryDto;
  defaultUnit: CatalogUnitDto;
  availableUnits: CatalogUnitDto[];
  availableCutTypes: CatalogCutTypeDto[];
  minQuantity: number;
  imageUrl: string | null;
  active: boolean;
  allowCutType: boolean;
  wastePercent: number | null;
  priceByUnit: Record<string, number>;
  priceByCutAndUnit: Record<string, Record<string, number>>;
  pricePerUnit: number | null;
}

const CATEGORY_EMOJI: Record<string, string> = {
  FRUTAS: "🍎",
  LEGUMES: "🥕",
  HORTALICAS: "🥬",
  TEMPEROS: "🌿",
  OVOS: "🥚",
  CEREAIS: "🌾",
  SUCOS: "🧃",
  ESPECIAIS: "✨",
  PROCESSADOS: "📦",
};

function normalizeCategoryCode(code: string): Category {
  if (code === "HORTALIÇAS") return "HORTALICAS";
  if (code === "SUPERMERCADO") return "PROCESSADOS";
  return code;
}

function categoryEmoji(category: CatalogCategoryDto) {
  return category.icon?.trim() || CATEGORY_EMOJI[normalizeCategoryCode(category.code)] || "🌱";
}

function toCatalogCategory(category: CatalogCategoryDto): CatalogCategory {
  return {
    id: normalizeCategoryCode(category.code),
    code: normalizeCategoryCode(category.code),
    label: category.name,
    emoji: categoryEmoji(category),
    sortOrder: category.sortOrder,
    operatorOnly: category.operatorOnly,
    active: category.active,
  };
}

function toCatalogCutType(cutType: CatalogCutTypeDto): CatalogCutType {
  return {
    id: cutType.id,
    name: cutType.name,
    active: cutType.active,
  };
}

function toCatalogUnit(unit: CatalogUnitDto): CatalogUnit {
  return {
    id: unit.id,
    name: unit.name,
    conversionToKg: unit.conversionToKg,
    active: unit.active,
    group: unit.group ?? undefined,
  };
}

function toProduct(dto: CatalogProductDto, displayOrder: number, unitNameById?: Map<string, string>): Product {
  const categoryCode = normalizeCategoryCode(dto.category.code);
  const availableUnits = dto.availableUnits.length > 0 ? dto.availableUnits.map((unit) => unit.name) : [dto.defaultUnit.name];
  const availableCuts = dto.availableCutTypes.map((cutType) => cutType.name);

  // O backend envia priceByUnit chaveado por ID de unidade; o FE consome por NOME. Traduz aqui usando os
  // metadados de unidades (uma unidade precificada pode não estar em availableUnits, então nome vem dos
  // metadados globais, com fallback para o próprio dto).
  const localNameById = new Map<string, string>(unitNameById ?? []);
  localNameById.set(dto.defaultUnit.id, dto.defaultUnit.name);
  dto.availableUnits.forEach((u) => localNameById.set(u.id, u.name));
  const priceByUnitName: Record<string, number> = {};
  for (const [unitId, price] of Object.entries(dto.priceByUnit ?? {})) {
    const unitName = localNameById.get(unitId);
    if (unitName) priceByUnitName[unitName] = price;
  }

  return {
    id: dto.id,
    name: dto.name,
    category: categoryCode,
    emoji: categoryEmoji(dto.category),
    imageUrl: dto.imageUrl ?? undefined,
    isProcessed: categoryCode === "PROCESSADOS",
    defaultUnit: dto.defaultUnit.name,
    availableUnits,
    availableCuts,
    active: dto.active,
    displayOrder,
    wastePercent: dto.wastePercent ?? undefined,
    priceByUnit: priceByUnitName,
    priceByCutAndUnit: dto.priceByCutAndUnit ?? {},
    pricePerUnit: dto.pricePerUnit ?? undefined,
  };
}

export async function fetchCatalogMetadata(): Promise<CatalogMetadata> {
  const [categories, cutTypes, units] = await Promise.all([
    requestJson<CatalogCategoryDto[]>("/api/v1/catalog/categories"),
    requestJson<CatalogCutTypeDto[]>("/api/v1/catalog/cut-types"),
    requestJson<CatalogUnitDto[]>("/api/v1/catalog/units"),
  ]);

  return {
    categories: categories.map(toCatalogCategory).sort((a, b) => a.sortOrder - b.sortOrder || a.label.localeCompare(b.label)),
    cutTypes: cutTypes.map(toCatalogCutType).sort((a, b) => a.name.localeCompare(b.name)),
    units: units.map(toCatalogUnit).sort((a, b) => a.name.localeCompare(b.name)),
  };
}

export async function fetchCatalogUnits(): Promise<CatalogUnit[]> {
  const units = await requestJson<CatalogUnitDto[]>("/api/v1/catalog/units");
  return units.map(toCatalogUnit).sort((a, b) => a.name.localeCompare(b.name));
}

/** Payload de criação/edição de unidade. `group` nulo/"OUTRAS" = sem grupo (exibido em OUTRAS). */
export interface CatalogUnitInput {
  name: string;
  group: string | null;
  conversionToKg: number;
  active: boolean;
}

/** Produtos que usam uma unidade — base da confirmação de impacto global ao alterar conversionToKg. */
export interface CatalogUnitUsage {
  unitId: string;
  unitName: string;
  productCount: number;
  products: { id: string; code: number | null; name: string }[];
}

export async function createCatalogUnit(input: CatalogUnitInput): Promise<CatalogUnit> {
  const dto = await requestJson<CatalogUnitDto>("/api/v1/admin/units", { method: "POST", body: input });
  return toCatalogUnit(dto);
}

export async function updateCatalogUnit(id: string, input: CatalogUnitInput): Promise<CatalogUnit> {
  const dto = await requestJson<CatalogUnitDto>(`/api/v1/admin/units/${id}`, { method: "PUT", body: input });
  return toCatalogUnit(dto);
}

export async function fetchCatalogUnitUsage(id: string): Promise<CatalogUnitUsage> {
  return requestJson<CatalogUnitUsage>(`/api/v1/admin/units/${id}/usage`);
}

/**
 * Snapshot do catálogo do cliente autenticado. Quando `deliveryUnitId` é informado, os preços já saem da
 * tabela da Unidade de Entrega escolhida (mesma regra do checkout) — assim a vitrine mostra o preço certo
 * desde o início do pedido. Unidade de outro cliente é rejeitada pelo backend (não faz fallback silencioso).
 */
export async function fetchCatalogSnapshot(deliveryUnitId?: string): Promise<CatalogSnapshot> {
  const query = deliveryUnitId ? `?deliveryUnitId=${encodeURIComponent(deliveryUnitId)}` : "";
  const [{ categories, cutTypes, units }, products] = await Promise.all([
    fetchCatalogMetadata(),
    requestJson<CatalogProductDto[]>(`/api/v1/catalog/products${query}`),
  ]);

  return {
    categories,
    cutTypes,
    units,
    products: products.map((product, index) => toProduct(product, index, unitNameById(units))),
  };
}

/** Mapa id → nome das unidades, para traduzir priceByUnit (id-keyed no backend) para nome no FE. */
function unitNameById(units: CatalogUnit[]): Map<string, string> {
  return new Map(units.map((u) => [u.id, u.name]));
}

/**
 * Catálogo do ponto de vista de um cliente específico — usado pelo wizard de pedido manual.
 * Retorna só os produtos com preço cadastrado na price table do cliente.
 */
export async function fetchCatalogSnapshotForCustomer(
  customerId: string,
  deliveryUnitId?: string | null,
): Promise<CatalogSnapshot> {
  const query = deliveryUnitId ? `?deliveryUnitId=${encodeURIComponent(deliveryUnitId)}` : "";
  const [{ categories, cutTypes, units }, products] = await Promise.all([
    fetchCatalogMetadata(),
    requestJson<CatalogProductDto[]>(`/api/v1/admin/customers/${customerId}/catalog-products${query}`),
  ]);

  return {
    categories,
    cutTypes,
    units,
    products: products.map((product, index) => toProduct(product, index, unitNameById(units))),
  };
}
